import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './DateCropper.css'; // Réutiliser le CSS existant

const ZoneCropper = ({ imageUrl, onCropComplete, onClose, zoneType = 'date' }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(zoneType === 'date' ? 1.5 : 2); // Zoom différent selon le type
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  // Callback quand la zone de crop change
  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Fonction pour créer l'image croppée
  const createCroppedImage = useCallback(async () => {
    if (!imageUrl || !croppedAreaPixels) return null;

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const image = new Image();

      image.onload = () => {
        const { width, height, x, y } = croppedAreaPixels;

        // Zone plus large pour les dates manuscrites, plus précise pour les montants
        const enhancementFactor = zoneType === 'date' ? 1.5 : 1.2;
        const enhancedWidth = Math.min(width * enhancementFactor, image.width - x);
        const enhancedHeight = Math.min(height * enhancementFactor, image.height - y);
        const enhancedX = Math.max(0, x - (enhancedWidth - width) / 2);
        const enhancedY = Math.max(0, y - (enhancedHeight - height) / 2);

        canvas.width = enhancedWidth;
        canvas.height = enhancedHeight;

        // Appliquer la rotation si nécessaire
        if (rotation !== 0) {
          ctx.translate(enhancedWidth / 2, enhancedHeight / 2);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.translate(-enhancedWidth / 2, -enhancedHeight / 2);
        }

        ctx.drawImage(
          image,
          enhancedX,
          enhancedY,
          enhancedWidth,
          enhancedHeight,
          0,
          0,
          enhancedWidth,
          enhancedHeight
        );

        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      };

      image.onerror = () => reject(new Error('Impossible de charger l\'image'));
      
      // Gérer les CORS pour les images servies par le backend
      image.crossOrigin = 'anonymous';
      image.src = imageUrl;
    });
  }, [imageUrl, croppedAreaPixels, rotation, zoneType]);

  // Gérer la validation du crop
  const handleCropConfirm = async () => {
    try {
      const croppedImage = await createCroppedImage();
      if (croppedImage) {
        onCropComplete(croppedImage);
      } else {
        alert('Erreur lors du recadrage. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur lors du crop:', error);
      alert('Erreur lors du recadrage de l\'image: ' + error.message);
    }
  };

  // Configuration selon le type de zone
  const getZoneConfig = () => {
    if (zoneType === 'amount') {
      return {
        title: 'Recadrer la zone du montant',
        aspect: 2 / 1, // Format plus carré pour les montants
        instructions: [
          'Sélectionnez uniquement la zone contenant le montant',
          'Incluez le montant complet (euros et centimes)',
          'Évitez d\'inclure du texte non nécessaire autour',
          'Zoomez pour une sélection précise si nécessaire',
          'Utilisez la rotation si le ticket est penché'
        ],
      };
    } else {
      return {
        title: 'Recadrer la zone de la date',
        aspect: 3 / 1, // Format rectangulaire pour la date
        instructions: [
          'Sélectionnez uniquement la zone contenant la date manuscrite',
          'Incluez toute la date (jour/mois/année) dans le cadre',
          'Évitez d\'inclure du texte non nécessaire autour',
          'Zoomez pour une sélection précise si nécessaire',
          'Utilisez la rotation si le ticket est penché'
        ],
      };
    }
  };

  const config = getZoneConfig();

  if (!imageUrl) return null;

  return (
    <div className="date-cropper-overlay">
      <div className="date-cropper-container">
        <div className="date-cropper-header">
          <h3>{config.icon} {config.title}</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="date-cropper-content">
          <div className="cropper-area">
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={config.aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropCompleteCallback}
              cropShape="rect"
              showGrid={true}
              minZoom={0.5}
              maxZoom={10}
              zoomSpeed={0.5}
            />
          </div>

          <div className="cropper-controls">
            <button 
              className="instructions-toggle"
              onClick={() => setShowInstructions(!showInstructions)}
              title={showInstructions ? "Masquer les instructions" : "Afficher les instructions"}
            >
              <i className={`fa-solid ${showInstructions ? 'fa-eye-slash' : 'fa-info-circle'}`}></i>
              {showInstructions ? 'Masquer aide' : 'Aide'}
            </button>

            <div className="control-group">
              <label>Zoom:</label>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>

                <input
                  type="range"
                  value={zoom}
                  min={0.5}
                  max={10}
                  step={0.1}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  style={{flex: 1}}
                />
              </div>
              <small style={{fontSize: '0.8em', color: '#666', marginTop: '2px', textAlign: 'center'}}>
                {zoom.toFixed(1)}x
              </small>
            </div>

            <div className="control-group">
              <label>Rotation:</label>
              <input
                type="range"
                value={rotation}
                min={-45}
                max={45}
                step={1}
                onChange={(e) => setRotation(parseInt(e.target.value))}
              />
            </div>
          </div>

          <div className={`cropper-instructions ${showInstructions ? 'visible' : ''}`}>
            <p><strong>Instructions pour recadrer {zoneType === 'amount' ? 'le montant' : 'la date'} :</strong></p>
            <ul>
              {config.instructions.map((instruction, index) => (
                <li key={index}>{instruction}</li>
              ))}
            </ul>
            <div style={{marginTop: '10px', padding: '8px', background: '#fff3cd', borderRadius: '6px', fontSize: '0.85em'}}>
              <strong>Astuce :</strong> Cette image provient de votre justificatif déjà enregistré, pas besoin de reprendre une photo !
            </div>
          </div>

          <div className="cropper-actions">
            <button className="btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button 
              className="btn-primary" 
              onClick={handleCropConfirm}
              disabled={!croppedAreaPixels}
            >
              <i className="fa-solid fa-crop"></i>
              Recadrer et analyser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoneCropper; 
